import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * After section morph / DOM updates, keep `inert` aligned with `aria-expanded`.
   * If they drift, CSS (`&[inert] { grid-template-rows: 0fr }`) can hide the panel
   * while the trigger looks open, or block toggling.
   * Temporarily disables transitions so the panel doesn't animate during morph.
   */
  updatedCallback() {
    super.updatedCallback();
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    if (!(trigger instanceof HTMLElement) || !(content instanceof HTMLElement)) return;
    const expanded = trigger.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      content.style.transition = 'none';
      content.inert = false;
      content.offsetHeight;
      content.style.transition = '';
    } else {
      content.inert = true;
    }
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    const { disclosureTrigger: trigger, disclosureContent: content } = this.refs;
    const expanded = trigger.matches('[aria-expanded="true"]');
    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    content.inert = expanded;
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
